/**
 * M46 — Invite-accept landing page.
 *
 * Unauthenticated flow: invitee clicks the email link with token,
 * sees their pending role + partner, types their email to confirm
 * identity (defense against link-sharing), clicks Accept.
 *
 * On success they're routed to /login with clientId preserved so they
 * can sign in with partner credentials and get going.
 */
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

const HUB_API_URL =
  (import.meta.env.VITE_HUB_API_URL as string | undefined) ??
  "https://hub.sumo.com";

interface InviteSummary {
  partner_id: string;
  partner_name: string;
  email: string;
  role: string;
}

export function InviteAcceptPage() {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const [invite, setInvite] = useState<InviteSummary | null>(null);
  const [lookupError, setLookupError] = useState<string | null>(null);
  const [email, setEmail] = useState("");
  const [status, setStatus] = useState<"idle" | "accepting" | "done" | "error">(
    "idle",
  );
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  useEffect(() => {
    if (!token) return;
    const url = `${HUB_API_URL}/v1/partner-team/invites/${token}`;
    fetch(url)
      .then(async (res) => {
        if (!res.ok) {
          const text = await res.text().catch(() => "");
          setLookupError(`${res.status}: ${text.slice(0, 200)}`);
          return;
        }
        setInvite((await res.json()) as InviteSummary);
      })
      .catch((e) => setLookupError(String(e)));
  }, [token]);

  async function accept() {
    if (!token || !invite) return;
    setStatus("accepting");
    setErrorMessage(null);
    const res = await fetch(
      `${HUB_API_URL}/v1/partner-team/invites/${token}/accept`,
      {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ email: email.trim().toLowerCase() }),
      },
    );
    if (!res.ok) {
      const text = await res.text().catch(() => "");
      setStatus("error");
      setErrorMessage(`${res.status}: ${text.slice(0, 200)}`);
      return;
    }
    setStatus("done");
    setTimeout(() => navigate("/login"), 1500);
  }

  if (lookupError) {
    return (
      <Shell>
        <h1 className="text-2xl font-semibold">Invite unavailable</h1>
        <p className="mt-2 text-sm text-red-500">{lookupError}</p>
        <p className="mt-4 text-sm text-neutral-500">
          Ask the person who invited you to send a new link.
        </p>
      </Shell>
    );
  }

  if (!invite) {
    return (
      <Shell>
        <div className="h-8 animate-pulse rounded bg-neutral-200 dark:bg-neutral-800" />
      </Shell>
    );
  }

  if (status === "done") {
    return (
      <Shell>
        <h1 className="text-2xl font-semibold">You're in</h1>
        <p className="mt-3 text-sm text-neutral-600 dark:text-neutral-400">
          Welcome to {invite.partner_name}. Redirecting to login…
        </p>
      </Shell>
    );
  }

  return (
    <Shell>
      <h1 className="text-2xl font-semibold">
        Join {invite.partner_name}
      </h1>
      <p className="mt-2 text-sm text-neutral-600 dark:text-neutral-400">
        You've been invited as <span className="font-medium">{invite.role}</span>.
        Confirm your email to accept.
      </p>

      <div className="mt-6 rounded-md bg-neutral-50 p-4 text-sm dark:bg-neutral-900">
        <p className="text-neutral-500">Invited email</p>
        <p className="mt-1 font-mono">{invite.email}</p>
      </div>

      <form
        className="mt-6 space-y-3"
        onSubmit={(e) => {
          e.preventDefault();
          accept();
        }}
      >
        <label className="block">
          <span className="text-xs font-medium text-neutral-500">
            Type your email to confirm
          </span>
          <input
            type="email"
            autoComplete="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="mt-1 w-full rounded-md border border-neutral-300 bg-white px-3 py-2 text-sm dark:border-neutral-700 dark:bg-neutral-950"
            required
          />
        </label>
        <button
          type="submit"
          disabled={
            status === "accepting" ||
            email.trim().toLowerCase() !== invite.email.toLowerCase()
          }
          className="w-full rounded-md bg-emerald-500 py-2 text-sm font-medium text-white hover:bg-emerald-600 disabled:opacity-50"
        >
          {status === "accepting" ? "Accepting…" : "Accept invite"}
        </button>
        {errorMessage ? (
          <p className="text-xs text-red-500">{errorMessage}</p>
        ) : null}
      </form>
    </Shell>
  );
}

function Shell({ children }: { children: React.ReactNode }) {
  return (
    <div className="mx-auto max-w-md px-4 py-16">
      <div className="rounded-xl border border-neutral-200 bg-white p-6 dark:border-neutral-800 dark:bg-neutral-950">
        {children}
      </div>
    </div>
  );
}
